import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from '../../../services/axiosInterceptors';
import nipponBotApiEndpoints from '../../../services/nipponBotApiEndpoints';
import { theme as colorTheme } from '../../../Convoze.theme';

const SCHEDULE_DATE_VALUE_FORMAT = 'YYYY-MM-DD';
const SCHEDULE_DATE_DISPLAY_FORMAT = 'DD/MM/YYYY';
const primary = colorTheme('palette', 'primary').value;
const PRIMARY_HOVER = '#5f2ab8';
const tableHeadingSx = {
  textTransform: 'uppercase',
  fontWeight: 550,
  color: '#6b7280',
  fontSize: '0.7rem',
  backgroundColor: '#f9fafb',
  whiteSpace: 'nowrap'
};

const formatScheduleDateTime = (value) => {
  if (!value) return '-';
  const date = moment(value);
  return date.isValid() ? date.format('MMM D, YYYY · h:mm A') : '-';
};

const formatStatus = (status = '') => status.replace(/_/g, ' ');

const getOrdinal = (value) => {
  const number = Number(value || 0);
  if (!number) return '';
  const suffix = number % 10 === 1 && number % 100 !== 11
    ? 'st'
    : number % 10 === 2 && number % 100 !== 12
      ? 'nd'
      : number % 10 === 3 && number % 100 !== 13
        ? 'rd'
        : 'th';
  return `${number}${suffix}`;
};

const formatFriendlyCallFailureReason = (category = '', reason = '') => {
  const normalizedCategory = String(category || '').trim();
  const normalizedReason = String(reason || '').toLowerCase();
  if (!normalizedCategory && !normalizedReason) return '';
  if (normalizedCategory === 'no_pickup' || normalizedReason.includes('not picked') || normalizedReason.includes('no answer')) {
    return 'candidate did not pick up';
  }
  if (normalizedCategory === 'disconnect' || normalizedReason.includes('disconnect') || normalizedReason.includes('hangup')) {
    return 'call got disconnected';
  }
  if (normalizedCategory === 'short_call' || normalizedReason.includes('less than 5 seconds')) {
    return 'call ended too quickly';
  }
  if (normalizedCategory === 'validation_error') {
    return reason || 'candidate phone number is missing';
  }
  if (normalizedCategory === 'sip_pending_timeout' || normalizedReason.includes('still being confirmed') || normalizedReason.includes('waiting for call status')) {
    return 'call status is still being confirmed';
  }
  if (normalizedCategory === 'unknown' || normalizedReason.includes('no sip messages') || normalizedReason.includes('sip data') || normalizedReason.includes('awaiting sip')) {
    return 'call was not connected';
  }
  return reason || 'call could not be completed';
};

const getBatchTone = (status = '') => {
  if (status === 'completed') return 'success';
  if (status === 'completed_with_failures') return 'warning';
  if (status === 'success') return 'success';
  if (status === 'submitted' || status === 'dispatched' || status === 'rescheduled') return 'info';
  if (status === 'failed') return 'error';
  if (status === 'running') return 'info';
  if (status === 'queued' || status === 'calling') return 'info';
  return 'default';
};

const getRunDisplayStatus = (run = {}) => {
  if (run.finalStatus === 'success') return 'success';
  if (run.status === 'pending' && Number(run.attemptCount || 0) > 0) return 'rescheduled';
  if (['queued', 'calling', 'submitted'].includes(run.status)) return 'dispatched';
  return run.status || 'pending';
};

const getRunDisplayLabel = (run = {}) => {
  const status = getRunDisplayStatus(run);
  const labels = {
    pending: 'Scheduled',
    queued: 'Dispatched',
    calling: 'Dispatched',
    submitted: 'Dispatched',
    dispatched: 'Dispatched',
    rescheduled: 'Rescheduled',
    success: 'Completed',
    failed: 'Failed'
  };
  return labels[status] || capitalizeFirst(formatStatus(status));
};

const getRunDisplayDetail = (run = {}) => {
  const attemptCount = Number(run.attemptCount || 0);
  const maxAttempts = Number(run.maxAttempts || 3);
  const friendlyReason = formatFriendlyCallFailureReason(run.failureCategory, run.failureReason || run.errorMessage || '');
  const retryAt = run.nextRetryAt ? new Date(run.nextRetryAt) : null;
  const retryAtLabel = retryAt && !Number.isNaN(retryAt.getTime())
    ? retryAt.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
    : '';

  if (run.finalStatus === 'success') return 'Call completed successfully.';
  if (run.status === 'failed') {
    const reason = friendlyReason || 'call could not be completed';
    return attemptCount >= maxAttempts
      ? `All ${maxAttempts} call attempts failed because ${reason}.`
      : `Call failed because ${reason}.`;
  }
  if (run.status === 'pending' && attemptCount > 0) {
    if (retryAtLabel && attemptCount < maxAttempts) {
      return `Call failed. Reattempting on ${retryAtLabel}.`;
    }
    const reason = friendlyReason || 'call could not be completed';
    return `${getOrdinal(attemptCount)} try failed because ${reason}. Retrying now.`;
  }
  if (run.failureCategory === 'sip_pending_timeout' || String(run.failureReason || '').toLowerCase().includes('still being confirmed')) {
    return 'Call was dispatched. Status is still being confirmed.';
  }
  if (['queued', 'calling', 'submitted'].includes(run.status)) {
    return `Call attempt ${Math.max(attemptCount, 1)} of ${maxAttempts} is in progress.`;
  }
  return 'Call is scheduled.';
};

const TONE_STYLES = {
  success: { color: '#1e7e34', bg: '#e6f4ea' },
  warning: { color: '#b45309', bg: '#fef3e2' },
  error: { color: '#c62828', bg: '#fdecea' },
  info: { color: '#0277bd', bg: '#e3f2fd' },
  default: { color: '#616161', bg: '#f0f0f0' }
};

const capitalizeFirst = (text = '') => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

const normalizeText = (value = '') => String(value || '').trim().toLowerCase();

const getReadinessKey = (candidate = {}) => {
  if (candidate.hasInterviewRecording) return 'ready';
  if (candidate.hasResume) return 'needs_review';
  return 'not_ready';
};

const getReadinessDisplay = (candidate = {}) => {
  const readiness = getReadinessKey(candidate);
  if (readiness === 'ready') return 'Ready';
  if (readiness === 'needs_review') return 'Needs Review';
  return 'Not Ready';
};

const getReadinessTone = (candidate = {}) => {
  const readiness = getReadinessKey(candidate);
  if (readiness === 'ready') return 'success';
  if (readiness === 'needs_review') return 'info';
  return 'default';
};

const getProfileStatusTone = (status = '') => {
  const normalized = normalizeText(status);
  if (normalized === 'completed') return 'success';
  if (normalized === 'failed') return 'error';
  if (normalized === 'processing') return 'warning';
  return 'default';
};

const getScheduleSummary = (schedule = {}) => {
  const runs = Array.isArray(schedule.runs) ? schedule.runs : [];
  const counts = schedule.summaryCounts || {};
  const completed = Number(counts.completed || 0);
  const failed = Number(counts.failed || 0);
  const running = Number(counts.running || 0);
  const pending = Number(counts.pending || 0);
  const total = runs.length || completed + failed + running + pending;
  return { completed, failed, running, pending, total };
};

const StatusChip = ({ label, tone }) => {
  const style = TONE_STYLES[tone] || TONE_STYLES.default;
  return (
    <Chip
      size="small"
      label={capitalizeFirst(label)}
      variant="outlined"
      sx={{
        fontWeight: 600,
        borderWidth: 1.5,
        borderColor: style.color,
        color: style.color,
        backgroundColor: style.bg
      }}
    />
  );
};

const buildScheduleIso = (dateValue, timeValue) => {
  if (!dateValue || !timeValue) return '';
  const composed = new Date(`${dateValue}T${timeValue}`);
  return Number.isNaN(composed.getTime()) ? '' : composed.toISOString();
};

const getTodayDateValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = `${today.getMonth() + 1}`.padStart(2, '0');
  const day = `${today.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function InterviewsTab({ onMessage, onRefresh }) {
  const token = localStorage.getItem('token');
  const decodedToken = token ? jwtDecode(token) : {};
  const userRole = decodedToken?.userRole || decodedToken?.role || '';
  const canManage = ['Manager', 'Admin'].includes(userRole);
  const canManageAgents = ['Manager', 'Admin', 'Curator'].includes(userRole);
  const isCurator = userRole === 'Curator';

  const [loading, setLoading] = useState(true);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [agentSubmitting, setAgentSubmitting] = useState(false);
  const [retryingScheduleId, setRetryingScheduleId] = useState('');
  const [deletingScheduleId, setDeletingScheduleId] = useState('');
  const [refreshingSchedules, setRefreshingSchedules] = useState(false);
  const [confirmDeleteRun, setConfirmDeleteRun] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [agents, setAgents] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [activeTab, setActiveTab] = useState('schedule');
  const [candidateSearch, setCandidateSearch] = useState('');
  const [debouncedCandidateSearch, setDebouncedCandidateSearch] = useState('');
  const [candidateProfileFilter, setCandidateProfileFilter] = useState('all');
  const [candidateReadinessFilter, setCandidateReadinessFilter] = useState('all');
  const [candidatePage, setCandidatePage] = useState(0);
  const [candidateRowsPerPage, setCandidateRowsPerPage] = useState(10);
  const [scheduleSearch, setScheduleSearch] = useState('');
  const [scheduleStatusFilter, setScheduleStatusFilter] = useState('all');
  const [scheduleAgentFilter, setScheduleAgentFilter] = useState('all');
  const [scheduledPage, setScheduledPage] = useState(0);
  const [scheduledRowsPerPage, setScheduledRowsPerPage] = useState(10);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    agentMappingId: '',
    scheduleDate: '',
    scheduleTime: '',
    waitIntervalMinutes: 20,
    maxConcurrentCalls: 1,
    includePreviousCallSummary: false
  });
  const [agentForm, setAgentForm] = useState({
    id: '',
    name: '',
    agentId: '',
    agentPhoneNumberId: '',
    isActive: true
  });

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedCandidateSearch(candidateSearch);
    }, 500);
    return () => clearTimeout(handler);
  }, [candidateSearch]);

  useEffect(() => {
    setCandidatePage(0);
  }, [debouncedCandidateSearch, candidateProfileFilter, candidateReadinessFilter, candidateRowsPerPage]);

  useEffect(() => {
    setScheduledPage(0);
  }, [scheduleSearch, scheduleStatusFilter, scheduleAgentFilter, scheduledRowsPerPage]);

  useEffect(() => {
    let active = true;

    const loadCandidates = async (query = '') => {
      setCandidateLoading(true);
      try {
        const response = await axiosInstance.get(nipponBotApiEndpoints.schedulingCandidates, {
          headers: authHeaders,
          params: { q: query }
        });
        if (active) setCandidates(response?.data?.data || []);
      } catch (error) {
        if (active) onMessage?.(error?.response?.data?.message || 'Failed to load Nippon interview candidates.', 'error');
      } finally {
        if (active) setCandidateLoading(false);
      }
    };

    loadCandidates(debouncedCandidateSearch);
    return () => { active = false; };
  }, [authHeaders, debouncedCandidateSearch]);

  useEffect(() => {
    let active = true;

    const loadAgents = async () => {
      const response = await axiosInstance.get(nipponBotApiEndpoints.elevenLabsAgents, { headers: authHeaders });
      if (active) setAgents(response?.data?.data || []);
    };

    const loadSchedules = async () => {
      const response = await axiosInstance.get(nipponBotApiEndpoints.outboundSchedules, { headers: authHeaders });
      if (active) setSchedules(response?.data?.data || []);
    };

    const loadAll = async () => {
      setLoading(true);
      try {
        await Promise.all([loadAgents(), loadSchedules()]);
      } catch (error) {
        if (active) onMessage?.(error?.response?.data?.message || 'Failed to load outbound interview scheduler data.', 'error');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadAll();
    return () => { active = false; };
  }, [authHeaders]);

  const selectedCandidatesCount = selectedCandidateIds.length;
  const filteredCandidates = useMemo(() => candidates.filter((candidate) => {
    const profileStatus = normalizeText(candidate.resumeExtractionStatus);
    const readiness = getReadinessKey(candidate);
    const matchesProfile = candidateProfileFilter === 'all' || profileStatus === candidateProfileFilter;
    const matchesReadiness = candidateReadinessFilter === 'all' || readiness === candidateReadinessFilter;
    return matchesProfile && matchesReadiness;
  }), [candidates, candidateProfileFilter, candidateReadinessFilter]);

  const paginatedCandidates = useMemo(() => (
    filteredCandidates.slice(
      candidatePage * candidateRowsPerPage,
      candidatePage * candidateRowsPerPage + candidateRowsPerPage
    )
  ), [filteredCandidates, candidatePage, candidateRowsPerPage]);

  const scheduleAgentOptions = useMemo(() => (
    [...new Set(schedules.map((schedule) => schedule.agentSnapshot?.name).filter(Boolean))]
  ), [schedules]);

  const scheduledInterviewRows = useMemo(() => schedules.flatMap((schedule) => {
    const runs = Array.isArray(schedule.runs) ? schedule.runs : [];
    if (!runs.length) {
      return [{
        id: `${schedule.id}-empty`,
        scheduleId: schedule.id,
        scheduledAt: schedule.scheduledAt,
        scheduleStatus: schedule.status || '',
        agentName: schedule.agentSnapshot?.name || '',
        run: null,
        candidateName: '',
        sapCode: '',
        attemptCount: 0,
        maxAttempts: 3,
        displayStatus: schedule.status || 'scheduled',
        displayLabel: capitalizeFirst(formatStatus(schedule.status || 'scheduled')),
        displayDetail: 'No candidates found for this batch.'
      }];
    }

    return runs.map((run) => {
      const displayStatus = getRunDisplayStatus(run);
      return {
        id: run.id || `${schedule.id}-${run.candidateId || run.candidateName}`,
        scheduleId: schedule.id,
        scheduledAt: run.nextRetryAt || schedule.scheduledAt,
        scheduleStatus: schedule.status || '',
        agentName: schedule.agentSnapshot?.name || '',
        run,
        candidateName: run.candidateName || '',
        sapCode: run.sapCode || '',
        attemptCount: Number(run.attemptCount || 0),
        maxAttempts: Number(run.maxAttempts || 3),
        displayStatus,
        displayLabel: getRunDisplayLabel(run),
        displayDetail: getRunDisplayDetail(run)
      };
    });
  }), [schedules]);

  const scheduleStatusOptions = useMemo(() => (
    [...new Map(scheduledInterviewRows.map((row) => [row.displayStatus, row.displayLabel])).entries()]
  ), [scheduledInterviewRows]);

  const filteredScheduledInterviewRows = useMemo(() => scheduledInterviewRows.filter((row) => {
    const query = normalizeText(scheduleSearch);
    const matchesSearch = !query
      || normalizeText(row.agentName).includes(query)
      || normalizeText(row.scheduleStatus).includes(query)
      || normalizeText(row.displayStatus).includes(query)
      || normalizeText(row.displayLabel).includes(query)
      || normalizeText(row.candidateName).includes(query)
      || normalizeText(row.sapCode).includes(query);
    const matchesStatus = scheduleStatusFilter === 'all' || row.displayStatus === scheduleStatusFilter;
    const matchesAgent = scheduleAgentFilter === 'all' || row.agentName === scheduleAgentFilter;
    return matchesSearch && matchesStatus && matchesAgent;
  }), [scheduledInterviewRows, scheduleSearch, scheduleStatusFilter, scheduleAgentFilter]);

  const paginatedScheduledInterviewRows = useMemo(() => (
    filteredScheduledInterviewRows.slice(
      scheduledPage * scheduledRowsPerPage,
      scheduledPage * scheduledRowsPerPage + scheduledRowsPerPage
    )
  ), [filteredScheduledInterviewRows, scheduledPage, scheduledRowsPerPage]);

  const scheduledTotals = useMemo(() => schedules.reduce((totals, schedule) => {
    const summary = getScheduleSummary(schedule);
    return {
      scheduled: totals.scheduled + summary.pending,
      active: totals.active + summary.running,
      completed: totals.completed + summary.completed,
      failed: totals.failed + summary.failed
    };
  }, { scheduled: 0, active: 0, completed: 0, failed: 0 }), [schedules]);

  const visibleCandidateIds = paginatedCandidates.map((candidate) => candidate.id);
  const allVisibleSelected = visibleCandidateIds.length > 0 && visibleCandidateIds.every((id) => selectedCandidateIds.includes(id));
  const anyVisibleSelected = visibleCandidateIds.some((id) => selectedCandidateIds.includes(id));

  const handleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedCandidateIds((current) => current.filter((id) => !visibleCandidateIds.includes(id)));
      return;
    }

    setSelectedCandidateIds((current) => [...new Set([...current, ...visibleCandidateIds])]);
  };

  const toggleCandidate = (candidateId) => {
    setSelectedCandidateIds((current) => (
      current.includes(candidateId)
        ? current.filter((id) => id !== candidateId)
        : [...current, candidateId]
    ));
  };

  const resetAgentForm = () => setAgentForm({ id: '', name: '', agentId: '', agentPhoneNumberId: '', isActive: true });

  const loadAgents = async () => {
    const response = await axiosInstance.get(nipponBotApiEndpoints.elevenLabsAgents, { headers: authHeaders });
    setAgents(response?.data?.data || []);
  };

  const loadSchedules = async () => {
    const response = await axiosInstance.get(nipponBotApiEndpoints.outboundSchedules, { headers: authHeaders });
    setSchedules(response?.data?.data || []);
  };

  const handleRefreshSchedules = async () => {
    setRefreshingSchedules(true);
    try {
      await loadSchedules();
      onMessage?.('Scheduled interviews refreshed.', 'success');
    } catch (error) {
      onMessage?.(error?.response?.data?.message || 'Failed to refresh scheduled interviews.', 'error');
    } finally {
      setRefreshingSchedules(false);
    }
  };

  const handleSubmitAgent = async (event) => {
    event.preventDefault();
    if (!canManageAgents) return;

    setAgentSubmitting(true);
    try {
      const payload = {
        name: agentForm.name,
        agentId: agentForm.agentId,
        agentPhoneNumberId: agentForm.agentPhoneNumberId,
        isActive: agentForm.isActive
      };
      if (agentForm.id) {
        await axiosInstance.patch(`${nipponBotApiEndpoints.elevenLabsAgents}/${agentForm.id}`, payload, { headers: authHeaders });
        onMessage?.('Language updated successfully.', 'success');
      } else {
        await axiosInstance.post(nipponBotApiEndpoints.elevenLabsAgents, payload, { headers: authHeaders });
        onMessage?.('Language created successfully.', 'success');
      }
      resetAgentForm();
      await loadAgents();
    } catch (error) {
      onMessage?.(error?.response?.data?.status?.value || error?.response?.data?.message || 'Failed to save language.', 'error');
    } finally {
      setAgentSubmitting(false);
    }
  };

  const handleEditAgent = (agent) => {
    setAgentForm({
      id: agent.id,
      name: agent.name,
      agentId: agent.agentId,
      agentPhoneNumberId: agent.agentPhoneNumberId,
      isActive: agent.isActive !== false
    });
  };

  const handleToggleAgentActive = async (agent) => {
    if (!canManageAgents) return;
    try {
      await axiosInstance.patch(`${nipponBotApiEndpoints.elevenLabsAgents}/${agent.id}`, {
        name: agent.name,
        agentId: agent.agentId,
        agentPhoneNumberId: agent.agentPhoneNumberId,
        isActive: !agent.isActive
      }, { headers: authHeaders });
      onMessage?.(`Agent ${!agent.isActive ? 'activated' : 'deactivated'} successfully.`, 'success');
      await loadAgents();
    } catch (error) {
      onMessage?.(error?.response?.data?.status?.value || error?.response?.data?.message || 'Failed to update agent status.', 'error');
    }
  };

  const handleSubmitSchedule = async (event) => {
    event.preventDefault();
    if (!canManage) return;

    const scheduledAt = buildScheduleIso(scheduleForm.scheduleDate, scheduleForm.scheduleTime);
    if (!scheduledAt) {
      onMessage?.('Select a valid schedule time for today or a future date.', 'error');
      return;
    }

    setScheduleSubmitting(true);
    try {
      await axiosInstance.post(nipponBotApiEndpoints.outboundSchedules, {
        agentMappingId: scheduleForm.agentMappingId,
        scheduledAt,
        waitIntervalSeconds: Number(scheduleForm.waitIntervalMinutes) * 60,
        maxConcurrentCalls: Number(scheduleForm.maxConcurrentCalls),
        includePreviousCallSummary: scheduleForm.includePreviousCallSummary === true,
        candidateIds: selectedCandidateIds
      }, { headers: authHeaders });
      onMessage?.(`Scheduled ${selectedCandidatesCount} outbound interview call${selectedCandidatesCount === 1 ? '' : 's'} successfully.`, 'success');
      setSelectedCandidateIds([]);
      setScheduleForm((current) => ({ ...current, scheduleDate: '', scheduleTime: '' }));
      setScheduleDialogOpen(false);
      await loadSchedules();
      if (typeof onRefresh === 'function') onRefresh();
    } catch (error) {
      onMessage?.(error?.response?.data?.message || 'Failed to schedule outbound interviews.', 'error');
    } finally {
      setScheduleSubmitting(false);
    }
  };

  const handleRetryFailed = async (scheduleId) => {
    if (!canManage) return;
    setRetryingScheduleId(scheduleId);
    try {
      await axiosInstance.post(`${nipponBotApiEndpoints.outboundSchedules}/${scheduleId}/retry-failed`, {}, { headers: authHeaders });
      onMessage?.('Calls have been queued for another try.', 'success');
      await loadSchedules();
    } catch (error) {
      onMessage?.(error?.response?.data?.message || 'Failed to retry failed outbound calls.', 'error');
    } finally {
      setRetryingScheduleId('');
    }
  };

  const handleDeleteRun = async (row) => {
    if (!canManage) return;
    setConfirmDeleteRun(row);
  };

  const handleCancelDeleteSchedule = () => {
    if (deletingScheduleId) return;
    setConfirmDeleteRun(null);
  };

  const handleConfirmDeleteSchedule = async () => {
    if (!canManage || !confirmDeleteRun?.scheduleId || !confirmDeleteRun?.run?.id) return;

    setDeletingScheduleId(confirmDeleteRun.run.id);
    try {
      await axiosInstance.delete(`${nipponBotApiEndpoints.outboundSchedules}/${confirmDeleteRun.scheduleId}/runs/${confirmDeleteRun.run.id}`, { headers: authHeaders });
      onMessage?.('Scheduled outbound interview call deleted successfully.', 'success');
      await loadSchedules();
    } catch (error) {
      onMessage?.(error?.response?.data?.message || 'Failed to delete scheduled outbound call.', 'error');
    } finally {
      setDeletingScheduleId('');
      setConfirmDeleteRun(null);
    }
  };

  // Auto-polling disabled to avoid constant table refresh/repaint.

  if (loading) {
    return (
      <Paper sx={{ p: 3, boxShadow: 0, border: 1, borderColor: 'grey.200', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={24} />
        <Typography variant="body2">Loading Nippon outbound interview scheduler...</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Box>
        <Paper
          sx={{
            boxShadow: 0,
            border: 1,
            borderColor: 'grey.200',
            borderRadius: 2,
            overflow: 'hidden',
            px: 2,
            '& .MuiTabs-indicator': { backgroundColor: primary, height: 3 },
            '& .MuiTab-root': {
              minHeight: 48,
              py: 1,
              fontSize: '0.875rem',
              textTransform: 'none',
              fontWeight: 700,
              color: 'grey.600',
              '&.Mui-selected': { color: primary },
              '&:hover': { backgroundColor: `${primary}14` },
            },
            '& .MuiTouchRipple-root': { color: primary },
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(event, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            textColor="inherit"
          >
            <Tab value="schedule" label="Schedule Outbound Interviews" />
            <Tab value="scheduled" label="Scheduled Interviews" />
          </Tabs>
        </Paper>
      </Box>

      {activeTab === 'schedule' && (
        <Box sx={{ display: 'grid', gap: 3 }}>
          {!canManage && (
            <Alert severity="info">
              Curator access is read-only here. Managers and Admins can create schedules and manage languages.
            </Alert>
          )}

          <Paper sx={{ p: 3, boxShadow: 0, border: 1, borderColor: 'grey.200', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6">Candidates</Typography>
                <Typography variant="body2" color="text.secondary">Choose the profiles to include in this outbound interview batch.</Typography>
              </Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {candidateLoading ? <CircularProgress size={20} /> : null}
                <Button
                  variant="contained"
                  onClick={() => setScheduleDialogOpen(true)}
                  disabled={!canManage || selectedCandidatesCount === 0}
                  sx={{
                    textTransform: 'none',
                    backgroundColor: primary,
                    borderRadius: '12px',
                    '&:hover': { backgroundColor: PRIMARY_HOVER }
                  }}
                >
                  Schedule{selectedCandidatesCount > 0 ? ` (${selectedCandidatesCount})` : ''}
                </Button>
              </Stack>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 1.5, mb: 2 }}>
              <TextField
                size="small"
                label="Search candidates"
                value={candidateSearch}
                onChange={(event) => setCandidateSearch(event.target.value)}
                placeholder="Search by name, SAP code, email, or UUID"
                fullWidth
              />
              <FormControl size="small" fullWidth>
                <InputLabel id="candidate-profile-filter-label">Profile Status</InputLabel>
                <Select
                  labelId="candidate-profile-filter-label"
                  value={candidateProfileFilter}
                  label="Profile Status"
                  onChange={(event) => setCandidateProfileFilter(event.target.value)}
                >
                  <MenuItem value="all">All statuses</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel id="candidate-readiness-filter-label">Readiness</InputLabel>
                <Select
                  labelId="candidate-readiness-filter-label"
                  value={candidateReadinessFilter}
                  label="Readiness"
                  onChange={(event) => setCandidateReadinessFilter(event.target.value)}
                >
                  <MenuItem value="all">All readiness</MenuItem>
                  <MenuItem value="ready">Ready</MenuItem>
                  <MenuItem value="needs_review">Needs Review</MenuItem>
                  <MenuItem value="not_ready">Not Ready</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>{selectedCandidatesCount}</strong> candidates selected
              </Typography>
              <Button size="small" variant="outlined" onClick={handleSelectAllVisible} disabled={!paginatedCandidates.length} sx={{ textTransform: 'none' }}>
                {allVisibleSelected ? 'Clear visible' : 'Select all visible'}
              </Button>
            </Box>
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              {!candidates.length ? (
                <Alert severity="info" sx={{ m: 2 }}>No Nippon candidates matched this search.</Alert>
              ) : !filteredCandidates.length ? (
                <Alert severity="info" sx={{ m: 2 }}>No candidates match the selected filters.</Alert>
              ) : (
                <TableContainer sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox checked={allVisibleSelected} indeterminate={!allVisibleSelected && anyVisibleSelected} onChange={handleSelectAllVisible} />
                        </TableCell>
                        <TableCell sx={tableHeadingSx}>Candidate</TableCell>
                        <TableCell sx={tableHeadingSx}>SAP Code</TableCell>
                        <TableCell sx={tableHeadingSx}>Profile Status</TableCell>
                        <TableCell sx={tableHeadingSx}>Interview Readiness</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedCandidates.map((candidate) => {
                        const isSelected = selectedCandidateIds.includes(candidate.id);
                        return (
                          <TableRow key={candidate.id} hover selected={isSelected} sx={isSelected ? { '& td': { bgcolor: '#f0f7ff' } } : undefined}>
                            <TableCell padding="checkbox">
                              <Checkbox checked={isSelected} onChange={() => toggleCandidate(candidate.id)} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{candidate.fullName}</Typography>
                              <Typography variant="caption" color="text.secondary">{candidate.email || candidate.candidateUuid || '-'}</Typography>
                            </TableCell>
                            <TableCell>{candidate.sapCode || '-'}</TableCell>
                            <TableCell>
                              <StatusChip label={candidate.resumeExtractionStatus || 'Profile only'} tone={getProfileStatusTone(candidate.resumeExtractionStatus)} />
                            </TableCell>
                            <TableCell>
                              <StatusChip label={getReadinessDisplay(candidate)} tone={getReadinessTone(candidate)} />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <TablePagination
                    component="div"
                    count={filteredCandidates.length}
                    page={candidatePage}
                    onPageChange={(event, nextPage) => setCandidatePage(nextPage)}
                    rowsPerPage={candidateRowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setCandidateRowsPerPage(parseInt(event.target.value, 10));
                      setCandidatePage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50]}
                    labelRowsPerPage="Rows per page"
                    sx={{
                      borderTop: '1px solid #EEEEEE',
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { m: 0 }
                    }}
                  />
                </TableContainer>
              )}
            </Paper>
          </Paper>
        </Box>
      )}

      {activeTab === 'schedule' && isCurator && (
        <Paper component="form" onSubmit={handleSubmitAgent} sx={{ p: 3, boxShadow: 0, border: 1, borderColor: 'grey.200', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Manage Languages</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add the display name, agent ID, and agent phone number ID here so scheduling can use named languages instead of raw IDs.
          </Typography>

          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr auto auto' }, alignItems: 'center', mb: 2 }}>
            <TextField label="Display Name" value={agentForm.name} onChange={(event) => setAgentForm((current) => ({ ...current, name: event.target.value }))} disabled={!isCurator} fullWidth />
            <TextField label="Agent ID" value={agentForm.agentId} onChange={(event) => setAgentForm((current) => ({ ...current, agentId: event.target.value }))} disabled={!isCurator} fullWidth />
            <TextField label="Agent Phone Number ID" value={agentForm.agentPhoneNumberId} onChange={(event) => setAgentForm((current) => ({ ...current, agentPhoneNumberId: event.target.value }))} disabled={!isCurator} fullWidth />
            <Button
              type="submit"
              variant="contained"
              disabled={!isCurator || agentSubmitting}
              sx={{
                textTransform: 'none',
                minWidth: 140,
                backgroundColor: primary,
                borderRadius: '12px',
                '&:hover': { backgroundColor: PRIMARY_HOVER }
              }}
            >
              {agentSubmitting ? <CircularProgress size={18} sx={{ color: 'white', mr: 1 }} /> : null}
              {agentForm.id ? 'Update Language' : 'Add Language'}
            </Button>
            <Button type="button" variant="outlined" onClick={resetAgentForm} disabled={agentSubmitting} sx={{ textTransform: 'none' }}>
              Reset
            </Button>
          </Box>

          {!agents.length ? (
            <Alert severity="info">No languages configured yet for Nippon Bot.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadingSx}>Name</TableCell>
                    <TableCell sx={tableHeadingSx}>Agent ID</TableCell>
                    <TableCell sx={tableHeadingSx}>Phone Number ID</TableCell>
                    <TableCell sx={tableHeadingSx}>Status</TableCell>
                    <TableCell sx={tableHeadingSx}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agents.map((agent) => (
                    <TableRow key={agent.id} hover>
                      <TableCell>{agent.name}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{agent.agentId}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{agent.agentPhoneNumberId}</TableCell>
                      <TableCell>
                        <Chip size="small" label={agent.isActive ? 'Active' : 'Inactive'} color={agent.isActive ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Button variant="outlined" size="small" onClick={() => handleEditAgent(agent)} sx={{ textTransform: 'none' }} disabled={!isCurator}>Edit</Button>
                          <Button variant="text" size="small" onClick={() => handleToggleAgentActive(agent)} sx={{ textTransform: 'none' }} disabled={!isCurator}>
                            {agent.isActive ? 'Deactivate' : 'Activate'}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {activeTab === 'scheduled' && (
        <Paper sx={{ p: 3, boxShadow: 0, border: 1, borderColor: 'grey.200', borderRadius: 2 }}>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
            {[
              ['Scheduled', scheduledTotals.scheduled],
              ['Active', scheduledTotals.active],
              ['Completed', scheduledTotals.completed],
              ['Failed', scheduledTotals.failed]
            ].map(([label, value]) => (
              <Paper key={label} sx={{ p: 2.5, boxShadow: 0, border: 1, borderColor: 'grey.200', borderRadius: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{value}</Typography>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
              </Paper>
            ))}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr auto' }, gap: 1.5, mb: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              label="Search"
              value={scheduleSearch}
              onChange={(event) => setScheduleSearch(event.target.value)}
              placeholder="Search candidate, SAP code, or language"
              fullWidth
            />
            <FormControl size="small" fullWidth>
              <InputLabel id="schedule-status-filter-label">Status</InputLabel>
              <Select
                labelId="schedule-status-filter-label"
                value={scheduleStatusFilter}
                label="Status"
                onChange={(event) => setScheduleStatusFilter(event.target.value)}
              >
                <MenuItem value="all">All statuses</MenuItem>
                {scheduleStatusOptions.map(([status, label]) => (
                  <MenuItem key={status} value={status}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="schedule-agent-filter-label">Language</InputLabel>
              <Select
                labelId="schedule-agent-filter-label"
                value={scheduleAgentFilter}
                label="Language"
                onChange={(event) => setScheduleAgentFilter(event.target.value)}
              >
                <MenuItem value="all">All agents</MenuItem>
                {scheduleAgentOptions.map((agentName) => (
                  <MenuItem key={agentName} value={agentName}>{agentName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              size="small"
              onClick={handleRefreshSchedules}
              disabled={refreshingSchedules}
              sx={{ textTransform: 'none', color: primary, borderColor: primary, whiteSpace: 'nowrap', '&:hover': { borderColor: primary, backgroundColor: `${primary}14` } }}
            >
              {refreshingSchedules ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>

          {!schedules.length ? (
            <Alert severity="info">No outbound interview batches have been scheduled yet.</Alert>
          ) : !filteredScheduledInterviewRows.length ? (
            <Alert severity="info">No scheduled interviews match the selected filters.</Alert>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadingSx}>Scheduled At</TableCell>
                    <TableCell sx={tableHeadingSx}>Language</TableCell>
                    <TableCell sx={tableHeadingSx}>Candidate</TableCell>
                    <TableCell sx={tableHeadingSx}>SAP Code</TableCell>
                    <TableCell sx={tableHeadingSx}>Attempts</TableCell>
                    <TableCell sx={tableHeadingSx}>Status</TableCell>
                    <TableCell sx={tableHeadingSx}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedScheduledInterviewRows.map((row) => {
                    const canRetry = row.displayStatus === 'failed' || row.scheduleStatus === 'failed';
                    return (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatScheduleDateTime(row.scheduledAt)}</TableCell>
                        <TableCell>{row.agentName || '-'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.candidateName || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 360 }}>
                            {row.displayDetail}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.sapCode || '-'}</TableCell>
                        <TableCell>
                          <Chip size="small" label={`${row.attemptCount} / ${row.maxAttempts}`} variant="outlined" sx={{ fontWeight: 700 }} />
                        </TableCell>
                        <TableCell>
                          <StatusChip label={row.displayLabel} tone={getBatchTone(row.displayStatus)} />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {canRetry ? (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleRetryFailed(row.scheduleId)}
                                disabled={!canManage || retryingScheduleId === row.scheduleId || deletingScheduleId === row.scheduleId}
                                sx={{ textTransform: 'none', color: primary, borderColor: primary, '&:hover': { borderColor: primary, backgroundColor: `${primary}14` } }}
                              >
                                {retryingScheduleId === row.scheduleId ? 'Retrying...' : 'Retry'}
                              </Button>
                            ) : null}
                            <Button
                              variant="text"
                              color="error"
                              size="small"
                              onClick={() => handleDeleteRun(row)}
                              disabled={!canManage || !row.run?.id || deletingScheduleId === row.run.id || retryingScheduleId === row.scheduleId}
                              sx={{ textTransform: 'none' }}
                            >
                              {deletingScheduleId === row.run?.id ? 'Deleting...' : 'Delete'}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={filteredScheduledInterviewRows.length}
                page={scheduledPage}
                onPageChange={(event, nextPage) => setScheduledPage(nextPage)}
                rowsPerPage={scheduledRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setScheduledRowsPerPage(parseInt(event.target.value, 10));
                  setScheduledPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage="Rows per page"
                sx={{
                  borderTop: '1px solid #EEEEEE',
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { m: 0 }
                }}
              />
            </TableContainer>
          )}
        </Paper>
      )}

      <Dialog
        open={scheduleDialogOpen}
        onClose={() => { if (!scheduleSubmitting) setScheduleDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
      >
        <Box component="form" onSubmit={handleSubmitSchedule}>
          <DialogTitle>Call Configuration</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Configure the outbound call for {selectedCandidatesCount} selected candidate{selectedCandidatesCount === 1 ? '' : 's'}.
            </DialogContentText>
            <Box sx={{ display: 'grid', gap: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel id="nippon-agent-select-label">Language</InputLabel>
                <Select
                  labelId="nippon-agent-select-label"
                  value={scheduleForm.agentMappingId}
                  label="Language"
                  onChange={(event) => setScheduleForm((current) => ({ ...current, agentMappingId: event.target.value }))}
                  disabled={!canManage}
                >
                  {agents.filter((agent) => agent.isActive).map((agent) => (
                    <MenuItem key={agent.id} value={agent.id}>{agent.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="Schedule Date"
                    format={SCHEDULE_DATE_DISPLAY_FORMAT}
                    value={scheduleForm.scheduleDate ? moment(scheduleForm.scheduleDate, SCHEDULE_DATE_VALUE_FORMAT) : null}
                    onChange={(value) => setScheduleForm((current) => ({
                      ...current,
                      scheduleDate: value && value.isValid() ? value.format(SCHEDULE_DATE_VALUE_FORMAT) : ''
                    }))}
                    minDate={moment(getTodayDateValue(), SCHEDULE_DATE_VALUE_FORMAT)}
                    disabled={!canManage}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </LocalizationProvider>
                <TextField
                  size="small"
                  type="time"
                  label="Schedule Time"
                  value={scheduleForm.scheduleTime}
                  onChange={(event) => setScheduleForm((current) => ({ ...current, scheduleTime: event.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  disabled={!canManage}
                  fullWidth
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <TextField
                  size="small"
                  type="number"
                  label="Wait Interval (min)"
                  value={scheduleForm.waitIntervalMinutes}
                  onChange={(event) => setScheduleForm((current) => ({ ...current, waitIntervalMinutes: event.target.value }))}
                  inputProps={{ min: 0 }}
                  disabled={!canManage}
                  fullWidth
                />
                <TextField
                  size="small"
                  type="number"
                  label="Max Concurrent Calls"
                  value={scheduleForm.maxConcurrentCalls}
                  onChange={(event) => setScheduleForm((current) => ({ ...current, maxConcurrentCalls: event.target.value }))}
                  inputProps={{ min: 1, max: 50 }}
                  disabled={!canManage}
                  fullWidth
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setScheduleDialogOpen(false)} disabled={scheduleSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!canManage || scheduleSubmitting || !scheduleForm.agentMappingId}
              sx={{
                textTransform: 'none',
                minWidth: 190,
                backgroundColor: primary,
                '&:hover': { backgroundColor: PRIMARY_HOVER }
              }}
            >
              {scheduleSubmitting ? <CircularProgress size={18} sx={{ color: 'white', mr: 1 }} /> : null}
              {scheduleSubmitting ? 'Scheduling...' : `Schedule ${selectedCandidatesCount} Interview${selectedCandidatesCount === 1 ? '' : 's'}`}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={Boolean(confirmDeleteRun)}
        onClose={handleCancelDeleteSchedule}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Scheduled Call</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete this scheduled outbound call for {confirmDeleteRun?.candidateName || 'this candidate'}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteSchedule} disabled={Boolean(deletingScheduleId)}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDeleteSchedule}
            disabled={Boolean(deletingScheduleId)}
          >
            {deletingScheduleId ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
